'use client';

import { ShieldCheck, ShieldAlert, ShieldX } from 'lucide-react';
import type { ReactNode } from 'react';
import type { Signal, Severity, Verdict } from '@/lib/web3/types';

const TONE: Record<
  Verdict,
  { ring: string; text: string; bg: string; bar: string; Icon: typeof ShieldCheck }
> = {
  safe: {
    ring: 'border-emerald-500/40',
    text: 'text-emerald-400',
    bg: 'bg-emerald-500/10',
    bar: 'bg-emerald-500',
    Icon: ShieldCheck,
  },
  caution: {
    ring: 'border-amber-500/40',
    text: 'text-amber-400',
    bg: 'bg-amber-500/10',
    bar: 'bg-amber-400',
    Icon: ShieldAlert,
  },
  danger: {
    ring: 'border-rose-500/50',
    text: 'text-rose-400',
    bg: 'bg-rose-500/10',
    bar: 'bg-rose-500',
    Icon: ShieldX,
  },
};

const SEVERITY_DOT: Record<Severity, string> = {
  info: 'bg-neutral-500',
  low: 'bg-amber-400/70',
  medium: 'bg-amber-400',
  high: 'bg-rose-400',
  critical: 'bg-rose-500',
};

const SEVERITY_LABEL: Record<Severity, string> = {
  info: 'Info',
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  critical: 'Critical',
};

interface Props {
  verdict: Verdict;
  /** Risk score 0-100 from engine. Displayed as safety = 100 - score. */
  score: number;
  label: string;
  subtitle?: string | null;
  signals: Signal[];
  children?: ReactNode;
  footer?: ReactNode;
}

export default function ResultPanel({
  verdict,
  score,
  label,
  subtitle,
  signals,
  children,
  footer,
}: Props) {
  const tone = TONE[verdict];
  const safety = 100 - score;

  return (
    <div className="mt-8 max-w-2xl mx-auto">
      {/* Header */}
      <div className={`border ${tone.ring} ${tone.bg} p-5`}>
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <span className="shrink-0">
              <tone.Icon className={`w-7 h-7 ${tone.text}`} />
            </span>
            <div className="min-w-0">
              <p className={`text-lg font-semibold ${tone.text}`}>{label}</p>
              {subtitle && (
                <p className="text-[11px] font-mono text-neutral-500 break-all mt-0.5">{subtitle}</p>
              )}
            </div>
          </div>
          <div className="text-right shrink-0">
            <p className={`text-3xl font-bold tabular-nums ${tone.text}`}>{safety}</p>
            <p className="text-[10px] uppercase tracking-wider text-neutral-500">safety / 100</p>
          </div>
        </div>

        {/* Safety bar */}
        <div className="mt-4 space-y-1">
          <div className="flex justify-between text-[10px] text-neutral-600">
            <span>Dangerous</span>
            <span>Safe</span>
          </div>
          <div className="h-2 w-full bg-black/40 overflow-hidden">
            <div
              className={`h-full transition-all duration-700 ${tone.bar}`}
              style={{ width: `${safety}%` }}
            />
          </div>
        </div>
      </div>

      {children}

      {/* Signals */}
      {signals.length > 0 && (
        <div className="mt-4">
          <p className="text-[10px] uppercase tracking-[0.15em] text-neutral-600 mb-2">
            Detection signals
          </p>
          <ul className="space-y-1.5">
            {signals.map((s, i) => (
              <li
                key={`${s.id}-${i}`}
                className="flex gap-3 border border-white/8 bg-black/30 p-3"
              >
                <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${SEVERITY_DOT[s.severity]}`} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-white">{s.title}</p>
                    <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 shrink-0 ${
                      s.severity === 'critical' || s.severity === 'high'
                        ? 'text-rose-400 bg-rose-500/10'
                        : s.severity === 'medium'
                        ? 'text-amber-400 bg-amber-500/10'
                        : 'text-neutral-500 bg-white/5'
                    }`}>
                      {SEVERITY_LABEL[s.severity]}
                    </span>
                  </div>
                  <p className="text-xs text-neutral-400 mt-0.5">{s.detail}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {footer}
    </div>
  );
}
